/**
 * Endless scoring. Deterministic given its inputs (no RNG) so a level is worth a
 * predictable amount. Points reward harder levels, deeper runs, fast decisions
 * and clean (no-hint) solves, all multiplied by the current win streak.
 */
import type { LevelDef } from './sim/types';

const TIER_RANK: Record<LevelDef['tier'], number> = {
  tutorial: 0, beginner: 1, easy: 2, medium: 3, hard: 4, expert: 5, brutal: 6, nightmare: 7,
};

export interface ScoreInput {
  level: LevelDef;
  index: number;
  /** Player think time from level open to breach, in ms. */
  thinkMs: number;
  usedHint: boolean;
  /** Win streak AFTER this clear (drives the multiplier). */
  streak: number;
}

export interface ScoreBreakdown {
  base: number;
  depth: number;
  speed: number;
  noHint: number;
  mult: number;
  total: number;
}

export function scoreLevel(i: ScoreInput): ScoreBreakdown {
  const base = 50 + TIER_RANK[i.level.tier] * 25;          // 50 (tutorial) .. 225 (nightmare)
  const depth = Math.floor(i.index / 10) * 10;             // +10 every 10 levels deep
  const speed = Math.max(0, Math.round(120 * (1 - i.thinkMs / 30_000))); // 120 instant → 0 at 30s
  const noHint = i.usedHint ? 0 : 50;                      // clean-solve bonus
  const mult = 1 + Math.min(Math.max(i.streak, 0), 20) * 0.1; // ×1 → ×3 at streak 20
  const total = Math.round((base + depth + speed + noHint) * mult);
  return { base, depth, speed, noHint, mult, total };
}

export const fmtScore = (n: number): string => Math.round(n).toLocaleString('en-US');
